import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { EllipsisVertical, Pencil, Pin, PinOff, Share, Trash2 } from "lucide-react";
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { toast } from "sonner";
import { Archive, Unarchive } from "@/components/icons/customIcons.tsx";
import { ThreadContactsMenuItem } from "@/components/threads/ThreadContacts.tsx";
import { ThreadDeleteDialog } from "@/components/threads/ThreadDeleteDialog.tsx";
import { useMarkThreadDone } from "@/components/threads/useMarkThreadDone.ts";
import { useOffersDelete } from "@/shared/useOffersDelete.ts";
import { Button, type ButtonProps } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import {
  threadSnapshotQueryOptions,
  useCreateThreadSnapshot,
  useSetThreadPinned,
} from "@/hooks/useThreads.tsx";
import { useMediaQuery } from "@/hooks/useMediaQuery.tsx";
import { cn } from "@/lib/utils.ts";
import type { GetThreadResp } from "@/types/thread.ts";

/**
 * Reveals a thread item's actions while the item is hovered, or while keyboard
 * focus is on the actions themselves. The item must carry `group/thread`.
 *
 * The focus half is scoped to this element rather than the whole row, so it
 * follows whatever this element happens to contain — on the compact row that is
 * the facepile as well as the two buttons, and tabbing to any of them should
 * bring the whole cluster with it.
 *
 * It tests `:focus-visible` rather than plain `:focus`, because these controls
 * open menus. Radix hands focus back to the trigger when a menu closes, so a
 * plain `focus-within` left the cluster stuck on screen after the pointer had
 * moved away. Keyboard focus still reveals it, which is the case the focus half
 * exists for.
 *
 * `pointer-events-none` is not decoration. On the compact row this cluster
 * overlays the date and the tail of the title, so an invisible control that
 * still took clicks would archive a thread the user meant to open.
 *
 * Only fine pointers get this. A touch screen has no hover to reveal with — and
 * iOS leaves the state stuck after a tap (BIG-5364) — so those layouts render
 * the controls outright rather than applying this.
 *
 * Written as one literal because Tailwind scans source text: a class name
 * assembled at runtime is never generated, and the actions would never appear.
 */
export const THREAD_ACTION_REVEAL =
  "opacity-0 pointer-events-none transition-opacity duration-150 group-hover/thread:opacity-100 group-hover/thread:pointer-events-auto has-[:focus-visible]:opacity-100 has-[:focus-visible]:pointer-events-auto";

/**
 * Lets the overflow menu start an inline rename on the surface that renders the
 * title it would edit.
 *
 * It travels by context rather than as a prop because the menu can sit four
 * levels below that surface — card, footer, status-specific footer variant, and
 * the archive cluster — none of which otherwise has any reason to know a thread
 * can be renamed. The row, where the menu is one level down, uses the same
 * provider so both surfaces read the same way.
 */
const ThreadRenameContext = createContext<(() => void) | null>(null);

/** Wrap a surface that can edit a thread's title in place. */
export const ThreadRenameProvider = ThreadRenameContext.Provider;

/**
 * Which list surface an action is rendered on. It names the Statsig event — the
 * two surfaces answer different questions, and whether the hover actions get
 * used at all is measured per surface, with `"card"` keeping the footer's
 * long-standing `thread-card-archive` event continuous — and it sets the glyph
 * scale, since a 36px row and a card have different room to give.
 */
export type ThreadActionSurface = "card" | "row";

/**
 * Glyph size per surface. The row's controls sit beside the compact status
 * badges, which are 12px marks in a 20px chip; at 20px the controls read nearly
 * twice their weight and the row looks lopsided. 16px closes most of that gap
 * while `size="action"`'s padding keeps the target at 24px, the smallest a
 * pointer target should be. The card has the room, so it keeps 20px.
 */
const actionIconClass = (surface: ThreadActionSurface) => (surface === "row" ? "size-4" : "size-5");

/**
 * Toggle a thread's pinned state, with the toast and error reporting every
 * surface that pins should share.
 */
export const useTogglePin = ({ thread }: { thread: GetThreadResp }) => {
  const pinMutation = useSetThreadPinned({ threadId: thread.id });
  const { mutateAsync } = pinMutation;
  const isPinned = thread.pinnedAt !== null;
  const threadId = thread.id;

  const togglePin = useCallback(async () => {
    try {
      await mutateAsync(!isPinned);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "useTogglePin", operation: isPinned ? "unpinThread" : "pinThread" },
        extra: { threadId },
      });
      errorToast(isPinned ? "Failed to unpin thread" : "Failed to pin thread");
      return;
    }
    confirmationToast(isPinned ? "Thread unpinned" : "Thread pinned to the top");
  }, [mutateAsync, isPinned, threadId]);

  return { isPinned, togglePin, isPending: pinMutation.isPending };
};

/**
 * One icon control on a thread card or row. Both live inside the item's `<Link>`,
 * so every click has to be stopped from navigating to the thread it acts on.
 */
const ThreadActionButton = ({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  statsigName,
  className,
  variant = "ghostPopover",
  size = "action",
  surface = "card",
  ref,
}: {
  icon: React.FC<{ className?: string }>;
  label: string;
  /** Mutating actions hand back a promise; one that opens a dialog returns nothing. */
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  statsigName: string;
  className?: string;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  surface?: ThreadActionSurface;
  /** For a control that opens an overlay, so focus has somewhere to return to. */
  ref?: React.Ref<HTMLButtonElement>;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn("shrink-0", className)}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          // The last net under every action in a list row. Each handler has
          // its own user-facing failure path; what is left here is the one
          // nobody planned for, and it should reach monitoring rather than a
          // console nobody is reading.
          Promise.resolve(onClick()).catch((error) => {
            Sentry.captureException(error, {
              tags: { component: "ThreadActionButton", operation: statsigName },
            });
          });
        }}
        disabled={disabled}
        aria-label={label}
        data-statsig-name={statsigName}
      >
        <Icon className={actionIconClass(surface)} />
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

/** Pin the thread to the top of the list, or return it to its date position. */
export const PinToggleButton = ({
  thread,
  className,
  surface = "card",
}: {
  thread: GetThreadResp;
  className?: string;
  surface?: ThreadActionSurface;
}) => {
  const { isPinned, togglePin, isPending } = useTogglePin({ thread });

  return (
    <ThreadActionButton
      icon={isPinned ? PinOff : Pin}
      label={isPinned ? "Unpin thread" : "Pin thread"}
      onClick={togglePin}
      disabled={isPending}
      statsigName={`thread-${surface}-${isPinned ? "unpin" : "pin"}`}
      className={className}
      surface={surface}
    />
  );
};

/**
 * Archive the thread, or bring an archived one back. Archiving routes through
 * `useMarkThreadDone` so this button behaves exactly like the `e` / ⌘D shortcuts
 * it sits beside, Undo toast and all.
 */
export const useToggleArchive = ({ thread }: { thread: GetThreadResp }) => {
  const { markDone, archiveMutation, unarchiveMutation } = useMarkThreadDone({
    threadId: thread.id,
  });
  const { mutateAsync: archiveAsync } = archiveMutation;
  const { mutateAsync: unarchiveAsync } = unarchiveMutation;
  const isArchived = thread.status === "completed";

  const handleUnarchive = useCallback(async () => {
    try {
      await unarchiveAsync(thread.id);
      confirmationToast("Unarchived", undefined, {
        actionText: "Undo",
        onAction: async (toastId) => {
          toast.dismiss(toastId);
          try {
            await archiveAsync();
          } catch (error) {
            Sentry.captureException(error, {
              tags: { component: "useToggleArchive", operation: "undoUnarchive" },
              extra: { threadId: thread.id },
            });
            errorToast("Failed to undo");
          }
        },
      });
    } catch (error) {
      // Louder than it was, because this is now the only way back: an archived
      // thread's visible control deletes, and unarchive lives a click deeper in
      // the overflow menu.
      Sentry.captureException(error, {
        tags: { component: "useToggleArchive", operation: "unarchiveThread" },
        extra: { threadId: thread.id },
      });
      errorToast("Failed to unarchive thread");
    }
  }, [unarchiveAsync, archiveAsync, thread.id]);

  return {
    isArchived,
    icon: isArchived ? Unarchive : Archive,
    /** Names the icon button, which has only a tooltip to say what it acts on. */
    label: isArchived ? "Unarchive this thread" : "Archive this thread",
    /** The menu already sits on one thread, so the row says only the verb. */
    menuLabel: isArchived ? "Unarchive" : "Archive",
    toggleArchive: isArchived ? handleUnarchive : markDone,
    isPending: isArchived ? unarchiveMutation.isPending : archiveMutation.isPending,
  };
};

type ThreadActionProps = {
  thread: GetThreadResp;
  className?: string;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  surface?: ThreadActionSurface;
};

/**
 * Delete the thread, behind the confirmation every other surface deletes
 * through. The dialog, not this button's position, is what makes a slipped
 * click survivable — which is why the action can sit out here at all.
 *
 * Its confirm event is named apart from the overflow menu's: both fire on the
 * same two surfaces, and one name for both would make a row-delete and a
 * menu-delete indistinguishable.
 */
const ThreadDeleteButton = ({
  thread,
  className,
  variant,
  size,
  surface = "card",
}: ThreadActionProps) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <ThreadActionButton
        ref={triggerRef}
        icon={Trash2}
        label="Delete this thread"
        onClick={() => setConfirmDeleteOpen(true)}
        statsigName={`thread-${surface}-primary-delete`}
        // Pinned on hover too. Every variant this renders with sets its own
        // hover text colour, which would drain the red at the moment the
        // pointer is on it.
        className={cn("text-destructive hover:text-destructive", className)}
        variant={variant}
        size={size}
        surface={surface}
      />
      <ThreadDeleteDialog
        thread={thread}
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        restoreFocusRef={triggerRef}
        statsigName={`thread-${surface}-primary-delete-confirm`}
      />
    </>
  );
};

/** The archive half of {@link ThreadPrimaryAction}: file the thread away, or
 *  put a filed one back. */
const ThreadArchiveButton = ({
  thread,
  className,
  variant,
  size,
  surface = "card",
}: ThreadActionProps) => {
  const { icon, label, toggleArchive, isPending } = useToggleArchive({ thread });

  return (
    <ThreadActionButton
      icon={icon}
      label={label}
      onClick={toggleArchive}
      disabled={isPending}
      statsigName={`thread-${surface}-archive`}
      className={className}
      variant={variant}
      size={size}
      surface={surface}
    />
  );
};

/**
 * The one action a thread offers beside its overflow menu in a list, which is
 * not the same action for every thread: a live one offers to be filed away,
 * and one {@link useOffersDelete} says is filed offers to be thrown out. Both
 * are the move the list is for — getting the thread off it — so they share the
 * slot rather than crowding it.
 *
 * Which arm renders picks the hooks that run with it, so a thread offering
 * Delete does not also build the two archive mutations it will never call.
 *
 * Unarchive is not lost in the trade. It stays in the overflow menu, a click
 * further from the irreversible action that replaced it here.
 */
export const ThreadPrimaryAction = (props: ThreadActionProps) =>
  useOffersDelete(props.thread) ? (
    <ThreadDeleteButton {...props} />
  ) : (
    <ThreadArchiveButton {...props} />
  );

/**
 * The share operation currently running for a thread, if any, keyed by thread.
 *
 * Keyed by thread rather than held per hook, because the thing that must happen
 * once is per thread: `fetchQuery` dedupes the *read*, so two callers who start
 * inside one round trip both see the same empty result and each concludes it
 * must mint a snapshot — leaving the thread with two live share links and the
 * user holding whichever wrote to the clipboard last.
 *
 * A ref would only cover one mounted control. Every list row and the detail
 * header build their own, and a row that unmounts and remounts on a list
 * refetch — which can land mid-lookup — arrives with a fresh one. Module scope
 * is what makes "once per thread" true across all of them.
 *
 * This narrows the window rather than closing it: a second tab, or another
 * device, still has its own module. The durable answer is for snapshot creation
 * to be idempotent per thread on the server, which is not this PR's to change.
 *
 * Entries are removed as they settle, so this holds only what is in flight.
 */
const sharesInFlight = new Map<number, Promise<string>>();

/**
 * Copy a share link for the thread, minting a snapshot only when there is not
 * one already. Sits beside `useTogglePin` so every surface that shares a thread
 * reports and toasts identically.
 */
export const useShareThread = ({ thread }: { thread: GetThreadResp }) => {
  const queryClient = useQueryClient();
  const snapshotMutation = useCreateThreadSnapshot({ threadId: thread.id });
  const { mutateAsync } = snapshotMutation;
  const threadId = thread.id;
  // Covers the lookup as well as the mutation. The mutation's own `isPending`
  // starts only once the lookup has decided a snapshot is needed, which leaves
  // the control live for the length of a network round trip — and the detail
  // header's Share is a standing button, not a menu item that closes on select,
  // so a second press lands naturally inside it.
  const [isLookingUp, setIsLookingUp] = useState(false);

  const share = useCallback(async () => {
    // Someone is already doing this for this thread. They will write the link
    // and toast; a second copy of both would be the only thing gained here.
    if (sharesInFlight.has(threadId)) return;

    setIsLookingUp(true);
    // Read the existing snapshot here rather than on mount. This hook is
    // mounted once per row, so a query would put one request on the wire for
    // every visible thread, for an action most of them never take.
    // `fetchQuery` still serves the cache within its staleTime, so sharing the
    // same thread twice does not re-fetch.
    const run = queryClient
      .fetchQuery(threadSnapshotQueryOptions(threadId))
      // Reuse that snapshot when there is one, so sharing twice hands out the
      // same link rather than minting a second one.
      .then(async (existing) => existing?.shareUrl ?? (await mutateAsync()).shareUrl);
    sharesInFlight.set(threadId, run);

    try {
      // Resolved into its own statement rather than inlined into the call:
      // `navigator.clipboard.writeText(await run)` evaluates the member access
      // first, so anywhere the clipboard is missing or blocked this throws
      // before the link is even awaited — and the `finally` below would then
      // clear the in-flight entry while the snapshot request was still running,
      // which is the exact duplicate this guard exists to prevent.
      const url = await run;
      await navigator.clipboard.writeText(url);
      confirmationToast("Link copied to clipboard");
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "useShareThread", operation: "shareThread" },
        extra: { threadId },
      });
      errorToast("Failed to create share link");
    } finally {
      sharesInFlight.delete(threadId);
      setIsLookingUp(false);
    }
  }, [queryClient, mutateAsync, threadId]);

  return { share, isPending: isLookingUp || snapshotMutation.isPending };
};

/**
 * The row's overflow menu: everything a thread can be done to, behind one
 * target, so a dense list can offer Share and Delete without growing a row of
 * glyphs for them. The thread's primary action stays outside as its own button
 * — it is the action taken from the list often enough to be worth a dedicated
 * click, and the menu repeats it so the menu is a complete answer on its own.
 * Where {@link useOffersDelete} turns that outside button into Delete, this
 * menu is the place Unarchive goes on living.
 *
 * Delete sits last and behind a separator. It is the one action here that
 * cannot be undone, and for a thread still in play that is worth the extra
 * click a slipped one would otherwise land on. The threads offering it outside
 * are the exception: there the same action is promoted, and the confirmation
 * dialog rather than the position is what keeps a slip survivable.
 */
export const ThreadRowActionsMenu = ({
  thread,
  className,
  variant = "ghostPopover",
  size = "action",
  surface = "row",
}: {
  thread: GetThreadResp;
  className?: string;
  /** Match whatever the host's other action wears — the card footer's Archive
   *  is an outlined icon button, so its menu has to be one too. */
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  surface?: ThreadActionSurface;
}) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Which modality last drove the menu. Radix restores focus to the trigger on
  // close and Chrome carries the menu item's `:focus-visible` across with it, so
  // after a mouse interaction the trigger reads as keyboard-focused, the reveal
  // stays on, and the cluster's `bg-popover` panel sits over the row's date
  // until something else takes focus. Restoring focus is right for keyboard and
  // wrong for the pointer, so it is skipped for the pointer only — the same
  // split, for the same reason, as `ThreadDetailPanel`'s overflow menu.
  const lastInputWasPointer = useRef(false);
  // Absent on any surface that renders the menu without a title to edit, which
  // is why Rename is conditional rather than always present and sometimes inert.
  const startRename = useContext(ThreadRenameContext);
  // Below `md` the row hides its facepile — a 44px line already carrying a badge
  // and a title has no room for faces — so the menu takes them, the same way the
  // detail header hands them over once it is too narrow to carry them. Above
  // `md` the row shows the faces itself and the menu says nothing about them.
  const rowShowsFacepile = useMediaQuery("md");
  const hasParticipants =
    (thread.card?.participants?.length ?? 0) > 0 ||
    (thread.card?.additionalParticipantCount ?? 0) > 0;
  const showContactsRow = surface === "row" && !rowShowsFacepile && hasParticipants;
  // Rename is the one item here whose result is a focused text field rather than
  // a mutation, so it cannot run while the menu is still closing: Radix's focus
  // scope tears down after `onSelect`, and the blur it deals out landed on the
  // title input the moment it mounted — which the input reads as "clicked away",
  // committing and closing the rename before a key could be pressed.
  //
  // So the selection is only recorded here and acted on in `onCloseAutoFocus`,
  // once the menu is gone and focus has settled.
  const renameSelectedRef = useRef(false);
  const { isPinned, togglePin, isPending: pinPending } = useTogglePin({ thread });
  const {
    icon: ArchiveIcon,
    menuLabel: archiveLabel,
    toggleArchive,
    isPending: archivePending,
  } = useToggleArchive({ thread });
  const { share, isPending: sharePending } = useShareThread({ thread });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            variant={variant}
            size={size}
            className={cn("shrink-0", className)}
            // The trigger sits inside the row's <Link>, so without this a click
            // opens the thread underneath the menu it just opened.
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onPointerDown={() => {
              lastInputWasPointer.current = true;
            }}
            onKeyDown={() => {
              lastInputWasPointer.current = false;
            }}
            aria-label="Thread actions"
            data-statsig-name={`thread-${surface}-menu`}
          >
            <EllipsisVertical className={actionIconClass(surface)} />
          </Button>
        </DropdownMenuTrigger>
        {/* Radix portals this to the document root, but a React portal bubbles
            its events through the React tree rather than the DOM one — so a
            click here still reaches the row's <Link> and would open the very
            thread the user was acting on. Guarded once for every item. */}
        <DropdownMenuContent
          align="end"
          className="min-w-44"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={() => {
            lastInputWasPointer.current = true;
          }}
          // Tracked on the content as well as the trigger, so a menu opened by
          // mouse but dismissed with Escape still counts as keyboard: the user's
          // hands moved, and focus should follow them.
          onKeyDown={() => {
            lastInputWasPointer.current = false;
          }}
          onCloseAutoFocus={(e) => {
            if (lastInputWasPointer.current || renameSelectedRef.current) e.preventDefault();
            if (renameSelectedRef.current) {
              renameSelectedRef.current = false;
              startRename?.();
            }
          }}
        >
          {showContactsRow && (
            <ThreadContactsMenuItem
              thread={thread}
              statsigName={`thread-${surface}-menu-contacts`}
            />
          )}
          <DropdownMenuItem
            onSelect={() => void togglePin()}
            disabled={pinPending}
            data-statsig-name={`thread-${surface}-menu-${isPinned ? "unpin" : "pin"}`}
          >
            {isPinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
            {isPinned ? "Unpin" : "Pin"}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => void toggleArchive()}
            disabled={archivePending}
            data-statsig-name={`thread-${surface}-menu-archive`}
          >
            <ArchiveIcon className="size-4" />
            {archiveLabel}
          </DropdownMenuItem>
          {/* After the two that file a thread, before the one that sends it on:
              Pin and Archive decide where the thread sits, Rename settles what
              it is called, and Share is the first item that involves anyone
              else. */}
          {startRename && (
            <DropdownMenuItem
              onSelect={() => {
                renameSelectedRef.current = true;
              }}
              data-statsig-name={`thread-${surface}-menu-rename`}
            >
              <Pencil className="size-4" />
              Rename
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={() => void share()}
            disabled={sharePending}
            data-statsig-name={`thread-${surface}-menu-share`}
          >
            <Share className="size-4" />
            Share
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => setConfirmDeleteOpen(true)}
            data-statsig-name={`thread-${surface}-menu-delete`}
          >
            <Trash2 className="size-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ThreadDeleteDialog
        thread={thread}
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        restoreFocusRef={triggerRef}
        statsigName={`thread-${surface}-delete-confirm`}
      />
    </>
  );
};
